import hyperdom from 'hyperdom'
import autocomplete from '../autocomplete.jsx'
import ValidationRules from './validationRules.js'
import LocalDate from '@booking-services/localDate'
import datePicker from '../datePicker.js'
import datesNotes from '../flights/datesNotes.jsx'
import transformPromisedFlightsResponse from './utils/transformPromisedFlightsResponse.js'
import * as gtmDataLayer from '../analytics/gtm/dataLayer.js'

const KEY_ENTER = 13

export default class Criteria {
  constructor (model, api) {
    this.model = model
    this.validator = new ValidationRules(model, model.exploreFlights)
    this.api = api
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  search () {
    const model = this.model
    const config = model.config
    const exploreFlights = model.exploreFlights
    const promisedFlights = model.promisedFlights
    const airports = model.airports
    const allAirports = model.allAirports
    promisedFlights.clearWarnings()

    const originAirportIndex =  allAirports.findIndex((airportSuggestion) => {

      if(!exploreFlights.departureAirport){
        const departureAirport =  allAirports.find(airport => airport.endsWith(exploreFlights.originAirportCode))
        Object.assign(exploreFlights, {departureAirport})

        return airportSuggestion === exploreFlights.departureAirport
      }

      return airportSuggestion === exploreFlights.departureAirport
    })
    const origin = airports[originAirportIndex]
    const tripStartAirport = promisedFlights.defaultAirports.start
    const tripEndAirport = promisedFlights.defaultAirports.end

    if (this.validator.validate() && origin) {
      exploreFlights.originAirportCode = origin.code
      model.showLoading()

      return this.api.promisedFlights({
        sellingCompanyCode: config.sellingCompanyCode,
        fromAirport: origin.code,
        departureDate: exploreFlights.getDepartDate(),
        destinationAirport: tripStartAirport,
        returningAirport: tripEndAirport,
      }).then(searchResults => {
        promisedFlights.searchResults = transformPromisedFlightsResponse(searchResults)
        if (promisedFlights.searchResults.length) {
          promisedFlights.searchMode = 'results'
        }
        model.hideLoading()
      }).catch(() => {
        promisedFlights.searchMode = 'noResults'
        model.hideLoading()
      })
    }
  }

  searchIfEnter (e) {
    if (e.keyCode === KEY_ENTER) {
      e.preventDefault()
      this.search()
    }
  }

  renderErrors (id) {
    const flights = this.model.exploreFlights
    const error = (flights.validationErrors || {})[id]
    if (error) {
      return <div class="o-form-item__warning">
        {error}
      </div>
    }
  }

  validClass (id) {
    const flights = this.model.exploreFlights
    const error = (flights.validationErrors || {})[id]
    return error ? 'invalid' : 'valid'
  }

  render () {
    const model = this.model
    const promisedFlights = model.promisedFlights
    const exploreFlights = model.exploreFlights
    const validator = this.validator
    const dateFormat = model.config.dateFormat
    const {placeholderCity} = model.config.flights

    return <div>
      <div className='promised-flights__search-criteria'>
        <div className='flights__airports-container'>
          {datesNotes(model)}
          <div class ="flights-search__form">
            <div class="o-form-item flights__airports-departure">
              <label for="departureAirport" class="o-form-item__label flights__label" key="departure-airport">
                Flying from
              </label>
              <i class="o-form-item__icon -new-ui_pin"></i>
              {autocomplete({
                id: "departureAirport",
                placeholder: placeholderCity ? `Try ${placeholderCity}` : 'City name or airport name',
                binding: [exploreFlights, 'departureAirport'],
                suggestions: model.homeCountryAirports,
                validate: validator.rules.departureAirport,
                onkeydown: this.searchIfEnter.bind(this),
                disableDebounce: this.model.disableLoadingTimeout,
                testClass: 'test-promised-flights-autocomplete',
              })}
              {this.renderErrors('departureAirport')}
            </div>
            <div class='flights__airports-icon-between'></div>
            <div class="o-form-item flights__airports-arrive">
              <label for="arriveAirport" class="o-form-item__label flights__label" key="arrive-airport">
                Flying to*
              </label>
              <i className="o-form-item__icon -new-ui_pin"></i>
              <input className={'o-form-item__txt-input ' + this.validClass('departureDate')}
                type="text"
                id="arriveAirport"
                readOnly
                placeholder={model.departure.startPlace.city}/>
              <div>* Flying to is defined by the trip start/end locations</div>
              {this.renderErrors('arriveAirport')}
            </div>
          </div>
        </div>
        <div className='flights__dates-container'>
          <div class="o-form-item flights__dates-departure">
            <label for="departureDate" class="o-form-item__label flights__label" key="departure-date">
              Flight dates
            </label>
            <i class="o-form-item__icon -new-ui_calendar"></i>
            {datePicker(
              <input class={'o-form-item__txt-input ' + this.validClass('departureDate')}
                type="text"
                id="departureDate"
                readOnly
                onblur={validator.rules.departureDate}
                onkeydown={this.searchIfEnter.bind(this)}
                placeholder='Departure date'
              />,
              {
                binding: [exploreFlights,'departureDate'],
                format: dateFormat,
                parseFormat: dateFormat,
                defaultDate: promisedFlights.defaultDepartDate,
              }
            )}
            {/*<div class='flights__dates-arrow-left'  onclick={(e) => {e.preventDefault();this.addDay(exploreFlights.departureDate,-1)}}></div>*/}
            {/*<div class='flights__dates-arrow-right'  onclick={(e) => {e.preventDefault();this.addDay(exploreFlights.departureDate,1)}}></div>*/}
            {this.renderErrors('departureDate')}
          </div>
          <div className='flights__dates-icon-between'></div>
          <div class="o-form-item flights__dates-arrive">
            <label for="return" class="o-form-item__label flights__label" key="return-date">
              &nbsp;
            </label>
            <i className="o-form-item__icon -new-ui_calendar"></i>
            {datePicker(
              <input class={'o-form-item__txt-input ' + this.validClass('returnDate')}
                type="text"
                id="return"
                readOnly
                onblur={validator.rules.returnDate}
                onkeydown={this.searchIfEnter.bind(this)}
                placeholder='Return date' />,
              {
                binding: [exploreFlights, 'returnDate'],
                format: dateFormat,
                parseFormat: dateFormat,
                defaultDate: promisedFlights.defaultReturnDate,
              }
            )}
            {this.renderErrors('returnDate')}
          </div>
        </div>
        <div class='flights__button'>
          <button type="button" onclick={this.search.bind(this)} class="btn-secondary">
            Find Flights
          </button>
        </div>
      </div>
    </div>
  }

  addDay (date, days) {
    const newDate = date ? new LocalDate(date).addDays(days).format('YYYY-MM-DD')  : undefined
    this.model.exploreFlights.departureDate = newDate
  }
}
